import React, { useEffect, useState } from 'react';
import { useTranslation } from "react-i18next";
import UserAPI from '../../../../axios/user';
import { PanelIcon } from '../../user-panel';
import { Check, Field } from '../panel-ui';

const EMPTY_PASSWORD = { old_password: "", new_password1: "", new_password2: "" };
const PASSWORD_FIELDS = [
    { name: 'old_password', label: 'old_password' },
    { name: 'new_password1', label: 'new_password' },
    { name: 'new_password2', label: 'confirm_password' },
];
// how long the bottom bar keeps saying "saved" before it hides
const SAVED_NOTICE_MS = 2500;
const STRENGTH_LABELS = ['strength_hint', 'strength_weak', 'strength_fair', 'strength_good', 'strength_strong'];

const passwordScore = (password) => !password ? 0
    : [password.length >= 8, /[A-Z]/.test(password), /\d/.test(password), /[^A-Za-z0-9]/.test(password)].filter(Boolean).length;

export const UserSecurity = () => {
    const { t } = useTranslation(["profile", "register"]);
    const [password, setPassword] = useState(EMPTY_PASSWORD);
    const [shown, setShown] = useState(false);
    // { ok, text }
    const [message, setMessage] = useState(null);

    const score = passwordScore(password.new_password1);
    const dirty = Object.values(password).some(Boolean);

    // a saved notice fades on its own; the bottom bar hides with it
    useEffect(() => {
        if (!message?.ok) return;
        const timer = setTimeout(() => setMessage(current => current?.ok ? null : current), SAVED_NOTICE_MS);
        return () => clearTimeout(timer);
    }, [message]);

    const messageText = message && (
        <span className={`hs-panel-status ${message.ok ? 'ok' : 'err'}`}>{message.ok && <Check />}{message.text}</span>
    );

    const change = (name, value) => {
        setPassword({ ...password, [name]: value.trim() });
        setMessage(null);
    };

    const fail = (text) => setMessage({ ok: false, text });

    const save = () => {
        if (!password.old_password || !password.new_password1 || !password.new_password2) return fail(t('fill_all_fields'));
        if (password.new_password1 !== password.new_password2) return fail(t('change_pass_err_confirm'));

        UserAPI.changePassword(password).then(() => {
            setMessage({ ok: true, text: t('change_pass_success') });
            setPassword(EMPTY_PASSWORD);
        }).catch((err) => {
            const errors = err.response?.status === 400 ? err.response.data?.new_password2 : null;
            fail(errors ? [].concat(errors).map(item => t(item, { ns: 'register' })).join(' ') : t('change_pass_err'));
        });
    };

    return (
        <section className='hs hs-panel-page'>
            <div className='hs-panel-card hs-panel-form'>
                <div className='hs-panel-form-head'>
                    <span className='hs-panel-form-icon'><PanelIcon name='lock' /></span>
                    <h2>{t('password')}</h2>
                </div>
                <div className='hs-panel-fields narrow'>
                    {PASSWORD_FIELDS.map(({ name, label }) => (
                        <Field key={name} label={t(label)} type={shown ? 'text' : 'password'} placeholder='••••••••'
                            value={password[name]} onChange={(e) => change(name, e.target.value)} />
                    ))}
                </div>
                <div className='hs-panel-strength'>
                    <div className={`hs-panel-strength-bars score-${score}`}>
                        {[1, 2, 3, 4].map(i => <span key={i} className={i <= score ? 'on' : ''}></span>)}
                    </div>
                    <span>{t(STRENGTH_LABELS[score])}</span>
                </div>
                <button className='hs-panel-text-btn' onClick={() => setShown(!shown)}>{t(shown ? 'hide_passwords' : 'show_passwords')}</button>
            </div>

            {/* shows up once something is typed, like the bar on the profile page */}
            {(dirty || message) &&
                <div className='hs-panel-savebar'>
                    {dirty
                        ? <>
                            <span className='hs-panel-savebar-text'>{message ? messageText : t('unsaved_changes')}</span>
                            <button className='hs-panel-primary' onClick={save}>{t('save_password')}</button>
                        </>
                        : messageText}
                </div>}
        </section>
    );
};
